/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import {
  AppAlert,
  Card,
  ContentWrapperComponent,
  SearchPaginateType,
  TaskSearchState,
} from "@advapacs/common";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Breadcrumb, Footer } from "../../parts";
import { sidebarAction } from "../../reducers/slices/sidebarSlice";
import { useSelector } from "react-redux";
import { IRootState } from "../../config/reducers";
import { Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import CriticalResultListContainer from "@src/pages/criticalResult/CriticalResultListContainer";
import {
  getCriticalResultList,
  getCriticalResultSearchParams,
} from "@src/pages/criticalResult/ducks/operations";
import { useAppDispatch } from "@src/config/store";
import { CriticalResultAction } from "@src/pages/criticalResult/ducks/slices";
import CriticalResultFilterForm from "@src/pages/criticalResult/FilterForm";

export enum TabKey {
  MY_CASE = "MY_CASE",
  COPY_TO_ME = "COPY_TO_ME",
}

const CriticalResultTabContainer: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { toggleSecondarySidebar } = useSelector(
    (state: IRootState) => state.sidebarReducer
  );
  const { isMobile } = useSelector(
    (state: IRootState) => state.responsiveReducer
  );

  const { searchState, errorMessage } = useSelector(
    (state: IRootState) => state.criticalResultReducer
  );

  const [tabKey, setTabKey] = useState(TabKey.MY_CASE);

  const sidebarClass = useMemo(() => {
    if (isMobile) {
      return toggleSecondarySidebar ? "" : "sidebar-mobile-expanded";
    }
    return toggleSecondarySidebar ? "" : "sidebar-collapsed";
  }, [isMobile, toggleSecondarySidebar]);

  const fetchData = useCallback(
    (search?: TaskSearchState) => {
      dispatch(
        getCriticalResultList(
          tabKey,
          getCriticalResultSearchParams({ ...searchState, ...search })
        )
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchState, tabKey]
  );

  const onSearch = () => {
    const searchParams = getCriticalResultSearchParams(searchState);
    dispatch(getCriticalResultList(tabKey, searchParams));
  };

  const handleSelect = (keyA: string | TabKey) => {
    if (tabKey === keyA) return;
    const searchStateValue = {
      ...searchState,
      page: 0,
      isOwnTaskSearch: TabKey.MY_CASE === keyA,
    };

    dispatch(CriticalResultAction.setSearchState({ ...searchStateValue }));
    dispatch(
      getCriticalResultList(
        keyA,
        getCriticalResultSearchParams({ ...searchState, ...searchStateValue })
      )
    );
    setTabKey(keyA as TabKey);
    dispatch(CriticalResultAction.setErrorMessage(null));
  };

  const searchPage = (search: SearchPaginateType) => {
    fetchData({ ...searchState, ...search } as TaskSearchState);
  };

  useEffect(() => {
    fetchData();
    return () => {
      dispatch(CriticalResultAction.clearData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div
        className={`sidebar sidebar-light sidebar-secondary sidebar-expand-md ${sidebarClass} `}
      >
        <CriticalResultFilterForm onSearch={onSearch} tabKey={tabKey} />
      </div>
      <ContentWrapperComponent
        breadcrumb={
          <Breadcrumb
            toggleSecondarySidebar={() => {
              dispatch(
                sidebarAction.toggleSecondarySidebar(!toggleSecondarySidebar)
              );
            }}
          />
        }
        footer={<Footer />}
      >
        <>
          {errorMessage && (
            <AppAlert.ErrorAlert
              error={errorMessage}
              onClose={() =>
                dispatch(CriticalResultAction.setErrorMessage(null))
              }
              dismissible
            />
          )}
          <Card title={t("critical_results")} bodyClass="p-0">
            <Tabs
              activeKey={tabKey}
              className="nav-tabs-bottom m-b-none"
              onSelect={handleSelect}
            >
              <Tab eventKey={TabKey.MY_CASE} title={t("my_critical_result")}>
                <CriticalResultListContainer
                  tabKey={TabKey.MY_CASE}
                  searchPage={searchPage}
                />
              </Tab>
              <Tab eventKey={TabKey.COPY_TO_ME} title={t("copied_to_me")}>
                <CriticalResultListContainer
                  tabKey={TabKey.COPY_TO_ME}
                  searchPage={searchPage}
                />
              </Tab>
            </Tabs>
          </Card>
        </>
      </ContentWrapperComponent>
    </>
  );
};

// @ts-ignore
export default CriticalResultTabContainer;
